// src/pages/settings/MeterConsoModal.tsx — modale "Consommations" d'un PDL/PCE, extraite de
// MetersTab (anciennement panneau inline). Un seul meter affiché à la fois : l'état de
// chargement/plage de dates est donc local au composant (plus de dictionnaire meterConsoMap).
//
// Deux onglets : "📊 Visualiser les données" (toujours) et "📥 Importer" (PDL source==="deepki"
// uniquement, contient DeepkiImportPanel). Pour deepki, la source des données bascule de
// /api/dataenr (report-api) vers une lecture directe de ts-ingest (getDeepkiReadings) —
// report-api n'a et ne doit avoir aucune connaissance du connecteur deepki (cf. docs/ts-ingest/ts-ingest.md).
// Le PCE (GAZ) appelle désormais directement ts-ingest (${tsIngestBaseUrl}/grdf/meter/...) dans la
// branche générique de loadConso() — /api/dataenr n'existe plus, report-api n'a plus aucune
// connaissance du GAZ (cf. CLAUDE.md, section "Migration ÉLEC + GAZ vers ts-ingest").
// Le sélecteur de plage (ConsoRangeControls) reste affiché dans les deux cas — pour deepki, il
// filtre simplement dans les données déjà importées, il ne relance pas d'import. À l'ouverture
// de la modale et juste après un import, la plage est calée automatiquement sur la plus petite
// période (au mois près) englobant les données importées (cf. applyImportRange). Le bouton
// "Purger les données" vit dans l'onglet Visualiser (pas dans DeepkiImportPanel) — l'action
// s'applique aux données affichées à cet endroit, pas au flux d'import lui-même. La confirmation
// de purge (openPurgeConfirm) fetch d'abord GET /deepki/counts/:pdlId pour afficher le vrai nombre
// de relevés journaliers ET horaires qui seront supprimés (les deux tables, cf. purgePdl côté
// serveur) — deepki_imports.rowCount seul ne reflète que le dernier import, pas l'état réel des
// deux tables (generateDailyFromHourly peut avoir peuplé la table jour même sans import direct).
//
// PDL — sélecteur "Pas" (jour/heure), pas un onglet : les options proposées dépendent de la
// source (pasOptionsForSource), pas de l'historique d'un import particulier — enedis ne fournit
// que du journalier ; deepki peut être lu aux deux pas indépendamment de ce qui a été importé en
// dernier (server/ts-ingest/connectors/deepki/db.ts stocke jour et heure dans deux tables
// séparées, purgées ensemble mais jamais l'une sans l'autre — cf. purgePdl). `lastImport.pas` ne
// sert donc qu'à présélectionner l'option juste après un import (ou à l'ouverture de la modale),
// jamais à restreindre la liste elle-même — un changement manuel de pas redemande explicitement
// les données au pas choisi (GET /deepki/readings/:pdlId?pas=…, cf. deepkiApi.ts). "Jour"
// réutilise SummaryStats/MensuelTable (agrégation par jour via dataMap, triviale si déjà
// journalier) ; "Heure" affiche entry.data brut (déjà au pas natif, non ré-agrégé) via
// HoraireTable.

import { useState, useCallback, useEffect } from "react";
import Modal from "./Modal";
import ConfirmModal from "../../components/ConfirmModal";
import LoadingScreen from "../../components/LoadingScreen";
import { useMinDuration } from "../../hooks/useMinDuration";
import { DeepkiImportPanel } from "./DeepkiImportPanel";
import type { SiteRecord } from "../../api/settingsApi";
import type { PdlRecord, PceRecord } from "../../api/metersApi";
import { getDeepkiReadings, getDeepkiLastImport, getDeepkiCounts, purgeDeepkiData, type DeepkiImportRecord } from "../../api/deepkiApi";
import { APP_CONFIG } from "../../config/app.config";

type Releve  = { t: number; i1: number; i2?: number };
type Periode = { debut: number; fin: number; energie: number; qualif: string };

interface ConsoEntry {
  data: [number, number][];
  loading: boolean;
  error: string | null;
  releves?:  Releve[];
  periodes?: Periode[];
  /** Pas réellement utilisé pour ce fetch (deepki uniquement) — sert à détecter un select "Pas"
   * déjà changé par l'utilisateur mais pas encore rafraîchi (cf. `dataFresh` dans le rendu). */
  pas?: Pas;
}

type ConsoSubTab = "mensuel" | "releves" | "index";
type Pas      = "jour" | "heure";
type ModalTab = "visualiser" | "importer";

const PAS_LABELS: Record<Pas, string> = { jour: "Jour", heure: "Heure" };

/** Pas disponibles pour la lecture des conso ELEC, selon la source du PDL (isDeepki). enedis ne
 * fournit que du journalier (server/ts-ingest/connectors/enedis) ; deepki peut être lu aux deux
 * pas, indépendamment du pas du dernier import (cf. commentaire en tête de fichier). */
function pasOptionsForSource(isDeepki: boolean): Pas[] {
  return isDeepki ? ["jour", "heure"] : ["jour"];
}

function fmtDate(epochS: number): string {
  return new Date(epochS * 1000).toISOString().substring(0, 10);
}

// deepki uniquement (cf. commentaire sur dataMap plus bas) : timestamps naïfs stockés comme UTC,
// donc getters UTC obligatoires pour ne pas décaler l'heure affichée selon le fuseau du navigateur.
function fmtDateTimeUTC(ts: number): string {
  const dt = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth()+1)}-${pad(dt.getUTCDate())} ${pad(dt.getUTCHours())}:${pad(dt.getUTCMinutes())}`;
}

// ── Sous-composants d'affichage — un seul meter à la fois, pas besoin d'être exportés ──────────

function TopTabs({ tabs, active, onChange }: {
  tabs: { id: ModalTab; label: string }[]; active: ModalTab; onChange: (t: ModalTab) => void;
}) {
  return (
    <div style={{display:"flex",gap:0,marginBottom:14,borderBottom:"1px solid #e0e0e0"}}>
      {tabs.map(t => {
        const isActive = active === t.id;
        return (
          <button key={t.id} onClick={()=>onChange(t.id)}
            style={{padding:"8px 16px",fontSize:13,fontFamily:"var(--ff-title)",fontWeight:isActive?700:500,
              background:"none",border:"none",borderBottom:isActive?"2px solid var(--green-dim)":"2px solid transparent",
              color:isActive?"var(--text-primary)":"var(--text-muted)",cursor:"pointer",marginBottom:-1}}>
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function ConsoRangeControls({
  fromYear, fromMonth, toYear, toMonth,
  setFromYear, setFromMonth, setToYear, setToMonth, setStale,
  stale, loading, canExport, onRefresh, onExportCsv, pasControl,
}: {
  fromYear: number; fromMonth: number; toYear: number; toMonth: number;
  setFromYear: (y: number) => void; setFromMonth: (m: number) => void;
  setToYear: (y: number) => void; setToMonth: (m: number) => void;
  setStale: (v: boolean) => void;
  stale: boolean; loading: boolean; canExport: boolean;
  onRefresh: () => void; onExportCsv: () => void;
  /** PDL deepki uniquement — cf. pasOptionsForSource. Rendu sur la même ligne que la période. */
  pasControl?: { options: Pas[]; value: Pas; onChange: (p: Pas) => void };
}) {
  const years  = Array.from({length:6}, (_, i) => new Date().getFullYear() - i);
  const months = Array.from({length:12}, (_, i) => i + 1);
  const key = (y: number, m: number) => `${y}-${String(m).padStart(2,"0")}`;

  return (
    <div style={{display:"flex",justifyContent:"flex-start",alignItems:"center",marginBottom:10,flexWrap:"wrap",gap:6}}>
      <div style={{display:"flex",gap:6,alignItems:"center"}}>
        <span style={{fontSize:11,color:"var(--text-muted)"}}>De</span>
        <select value={fromYear} onChange={e=>{const y=Number(e.target.value);setFromYear(y);setStale(true);if(key(y,fromMonth)>key(toYear,toMonth)){setToYear(y);setToMonth(fromMonth);}}}
          className="field" style={{marginBottom:0,fontSize:12,width:76,padding:"2px 4px"}}>
          {years.map(y=><option key={y} value={y}>{y}</option>)}
        </select>
        <select value={fromMonth} onChange={e=>{const mo=Number(e.target.value);setFromMonth(mo);setStale(true);if(key(fromYear,mo)>key(toYear,toMonth)){setToYear(fromYear);setToMonth(mo);}}}
          className="field" style={{marginBottom:0,fontSize:12,width:56,padding:"2px 4px"}}>
          {months.map(m=><option key={m} value={m}>{String(m).padStart(2,"0")}</option>)}
        </select>
        <span style={{fontSize:11,color:"var(--text-muted)"}}>à</span>
        <select value={toYear} onChange={e=>{const y=Number(e.target.value);setToYear(y);setStale(true);if(key(fromYear,fromMonth)>key(y,toMonth)){setFromYear(y);setFromMonth(toMonth);}}}
          className="field" style={{marginBottom:0,fontSize:12,width:76,padding:"2px 4px"}}>
          {years.map(y=><option key={y} value={y}>{y}</option>)}
        </select>
        <select value={toMonth} onChange={e=>{const mo=Number(e.target.value);setToMonth(mo);setStale(true);if(key(fromYear,fromMonth)>key(toYear,mo)){setFromYear(toYear);setFromMonth(mo);}}}
          className="field" style={{marginBottom:0,fontSize:12,width:56,padding:"2px 4px"}}>
          {months.map(m=><option key={m} value={m}>{String(m).padStart(2,"0")}</option>)}
        </select>
        {pasControl && (
          <>
            <span style={{fontSize:11,color:"var(--text-muted)"}}>Pas</span>
            <select value={pasControl.value} onChange={e=>{pasControl.onChange(e.target.value as Pas);setStale(true);}}
              className="field" style={{marginBottom:0,fontSize:12,width:90,padding:"2px 4px"}}>
              {pasControl.options.map(p=><option key={p} value={p}>{PAS_LABELS[p]}</option>)}
            </select>
          </>
        )}
        <button onClick={onRefresh} disabled={loading}
          title="Rafraîchir"
          className={stale && !loading ? "btn btn-sm" : "btn btn-ghost btn-sm"}
          style={{padding:"2px 8px",alignSelf:"stretch",display:"flex",alignItems:"center",fontSize:14,
            ...(stale && !loading ? {background:"var(--green-dim)",color:"#fff",borderColor:"var(--green-dim)"} : {})}}>
          {loading ? "…" : "↺"}
        </button>
        <button disabled={!canExport} title="Exporter CSV" className="btn btn-ghost btn-sm"
          style={{padding:"2px 8px",alignSelf:"stretch",display:"flex",alignItems:"center",fontSize:13}}
          onClick={onExportCsv}>
          ↓ CSV
        </button>
      </div>
    </div>
  );
}

function PceSubTabs({ active, onChange }: { active: ConsoSubTab; onChange: (t: ConsoSubTab) => void }) {
  const labels: Record<ConsoSubTab, string> = { mensuel:"Mensuel", releves:"Relevés", index:"Index" };
  return (
    <div style={{display:"flex",gap:0,marginBottom:10,borderBottom:"1px solid #e8e8e8"}}>
      {(["mensuel","releves","index"] as const).map(tab => {
        const isActive = active === tab;
        return (
          <button key={tab} onClick={()=>onChange(tab)}
            style={{padding:"5px 14px",fontSize:12,fontFamily:"var(--ff-title)",fontWeight:isActive?700:400,
              background:"none",border:"none",borderBottom:isActive?"2px solid var(--green-dim)":"2px solid transparent",
              color:isActive?"var(--green-dim)":"var(--text-muted)",cursor:"pointer",marginBottom:-1}}>
            {labels[tab]}
          </button>
        );
      })}
    </div>
  );
}

function HoraireTable({ data }: { data: [number, number][] }) {
  if (data.length === 0) {
    return <div style={{fontSize:12,color:"var(--text-faint)",fontStyle:"italic"}}>Aucune donnée horaire disponible pour cette période.</div>;
  }
  return (
    <div style={{overflowX:"auto"}}>
      <table style={{borderCollapse:"collapse",fontSize:12,fontFamily:"var(--ff-title)",maxWidth:320}}>
        <thead>
          <tr style={{background:"#efefef"}}>
            <th style={{padding:"5px 10px",textAlign:"left",borderBottom:"1px solid #e0e0e0",fontWeight:700}}>Date / Heure</th>
            <th style={{padding:"5px 10px",textAlign:"right",borderBottom:"1px solid #e0e0e0",fontWeight:700}}>kWh</th>
          </tr>
        </thead>
        <tbody>
          {data.map(([ts, v], ri) => (
            <tr key={ts} style={{background:ri%2===0?"#fff":"#f6f6f6"}}>
              <td style={{padding:"4px 10px",borderBottom:"1px solid #f0f0f0",color:"var(--text-muted)"}}>{fmtDateTimeUTC(ts)}</td>
              <td style={{padding:"4px 10px",textAlign:"right",borderBottom:"1px solid #f0f0f0",color:"var(--text-primary)",fontWeight:600}}>
                {v.toLocaleString("fr-FR",{maximumFractionDigits:3})}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SummaryStats({ totalKwh, dataCount, expectedCount, missingCount, unitLabel, unitLabelPl }: {
  totalKwh: number; dataCount: number; expectedCount: number; missingCount: number;
  unitLabel: string; unitLabelPl: string;
}) {
  return (
    <div style={{fontSize:12,color:"var(--text-muted)",marginBottom:10,display:"flex",gap:16,flexWrap:"wrap"}}>
      <span>Total : <strong>{Math.round(totalKwh).toLocaleString("fr-FR")} kWh</strong></span>
      <span>{dataCount} / {expectedCount} {expectedCount>1?unitLabelPl:unitLabel}</span>
      {missingCount > 0
        ? <span style={{color:"#c8740a"}}>⚠ {missingCount} {missingCount>1?unitLabelPl:unitLabel} manquant{missingCount>1?"s":""}</span>
        : dataCount > 0
          ? <span style={{color:"var(--green-dim)"}}>✓ Données complètes</span>
          : null}
    </div>
  );
}

function MensuelTable({ expectedDates, dataMap }: { expectedDates: string[]; dataMap: Map<string,number> }) {
  if (dataMap.size === 0) {
    return <div style={{fontSize:12,color:"var(--text-faint)",fontStyle:"italic"}}>Aucune donnée disponible pour cette période.</div>;
  }
  return (
    <div style={{overflowX:"auto"}}>
      <table style={{borderCollapse:"collapse",fontSize:12,fontFamily:"var(--ff-title)",maxWidth:320}}>
        <thead>
          <tr style={{background:"#efefef"}}>
            <th style={{padding:"5px 10px",textAlign:"left",borderBottom:"1px solid #e0e0e0",fontWeight:700}}>Date</th>
            <th style={{padding:"5px 10px",textAlign:"right",borderBottom:"1px solid #e0e0e0",fontWeight:700}}>kWh</th>
          </tr>
        </thead>
        <tbody>
          {expectedDates.map((d, ri)=>{
            const v = dataMap.get(d);
            const missing = v == null;
            return (
              <tr key={d} style={{background:missing?"#fff8f0":ri%2===0?"#fff":"#f6f6f6"}}>
                <td style={{padding:"4px 10px",borderBottom:"1px solid #f0f0f0",color:missing?"#c8740a":"var(--text-muted)"}}>{d}</td>
                <td style={{padding:"4px 10px",textAlign:"right",borderBottom:"1px solid #f0f0f0",color:missing?"#c8740a":"var(--text-primary)",fontWeight:missing?400:600}}>
                  {missing ? "—" : Math.round(v!).toLocaleString("fr-FR")}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RelevesTable({ periodes }: { periodes: Periode[] | undefined }) {
  if (!periodes || periodes.length === 0) {
    return <div style={{fontSize:12,color:"var(--text-faint)",fontStyle:"italic"}}>Aucun relevé disponible pour cette période.</div>;
  }
  return (
    <div style={{overflowX:"auto"}}>
      <table style={{borderCollapse:"collapse",fontSize:12,fontFamily:"var(--ff-title)"}}>
        <thead>
          <tr style={{background:"#efefef"}}>
            <th style={{padding:"5px 10px",textAlign:"left",borderBottom:"1px solid #e0e0e0",fontWeight:700}}>Début</th>
            <th style={{padding:"5px 10px",textAlign:"left",borderBottom:"1px solid #e0e0e0",fontWeight:700}}>Fin</th>
            <th style={{padding:"5px 10px",textAlign:"right",borderBottom:"1px solid #e0e0e0",fontWeight:700}}>kWh</th>
            <th style={{padding:"5px 10px",textAlign:"left",borderBottom:"1px solid #e0e0e0",fontWeight:700,color:"var(--text-muted)"}}>Qualité</th>
          </tr>
        </thead>
        <tbody>
          {periodes.map((p, ri) => (
            <tr key={p.debut} style={{background:ri%2===0?"#fff":"#f6f6f6"}}>
              <td style={{padding:"4px 10px",borderBottom:"1px solid #f0f0f0",color:"var(--text-muted)"}}>{new Date(p.debut).toISOString().substring(0,10)}</td>
              <td style={{padding:"4px 10px",borderBottom:"1px solid #f0f0f0",color:"var(--text-muted)"}}>{new Date(p.fin).toISOString().substring(0,10)}</td>
              <td style={{padding:"4px 10px",textAlign:"right",borderBottom:"1px solid #f0f0f0",color:"var(--text-primary)",fontWeight:600}}>{Math.round(p.energie).toLocaleString("fr-FR")}</td>
              <td style={{padding:"4px 10px",borderBottom:"1px solid #f0f0f0",color:"var(--text-faint)",fontSize:11}}>{p.qualif}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IndexTable({ releves }: { releves: Releve[] | undefined }) {
  if (!releves || releves.length === 0) {
    return <div style={{fontSize:12,color:"var(--text-faint)",fontStyle:"italic"}}>Aucun index disponible pour cette période.</div>;
  }
  return (
    <table style={{borderCollapse:"collapse",fontSize:12,fontFamily:"var(--ff-title)",maxWidth:320}}>
      <thead>
        <tr style={{background:"#efefef"}}>
          <th style={{padding:"5px 10px",textAlign:"left",borderBottom:"1px solid #e0e0e0",fontWeight:700}}>Date</th>
          <th style={{padding:"5px 10px",textAlign:"right",borderBottom:"1px solid #e0e0e0",fontWeight:700}}>Index (m³ brut)</th>
        </tr>
      </thead>
      <tbody>
        {releves.map((r, ri) => (
          <tr key={r.t} style={{background:ri%2===0?"#fff":"#f6f6f6"}}>
            <td style={{padding:"4px 10px",borderBottom:"1px solid #f0f0f0",color:"var(--text-muted)"}}>{new Date(r.t).toISOString().substring(0,10)}</td>
            <td style={{padding:"4px 10px",textAlign:"right",borderBottom:"1px solid #f0f0f0",color:"var(--text-primary)",fontWeight:600}}>
              {r.i1.toLocaleString("fr-FR")}
              {r.i2 != null && <span style={{color:"var(--text-muted)",marginLeft:8,fontWeight:400}}>/ {r.i2.toLocaleString("fr-FR")}</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Composant principal ──────────────────────────────────────────────────────────────────────

export function MeterConsoModal({ meter, isPdl, site, showToast, canPurgeDeepki, onClose }: {
  meter: PdlRecord | PceRecord;
  isPdl: boolean;
  site: SiteRecord | undefined;
  showToast: (msg: string) => void;
  /** Admin/super-admin uniquement — autorise le bouton "Purger les données" dans l'onglet Visualiser. */
  canPurgeDeepki: boolean;
  onClose: () => void;
}) {
  const isDeepki = isPdl && (meter as PdlRecord).source === "deepki";
  const isEngie  = (isPdl && (meter as PdlRecord).source === "engie") || (!isPdl && (meter as PceRecord).source === "engie");

  const [activeTab,      setActiveTab]      = useState<ModalTab>("visualiser");
  const [consoSubTab,    setConsoSubTab]    = useState<ConsoSubTab>("mensuel");
  const [pas,            setPas]            = useState<Pas>("jour");
  const [consoFromYear,  setConsoFromYear]  = useState(new Date().getFullYear());
  const [consoFromMonth, setConsoFromMonth] = useState(new Date().getMonth() + 1);
  const [consoToYear,    setConsoToYear]    = useState(new Date().getFullYear());
  const [consoToMonth,   setConsoToMonth]   = useState(new Date().getMonth() + 1);
  const [consoStale,     setConsoStale]     = useState(false);
  const [entry,          setEntry]          = useState<ConsoEntry | null>(null);
  const [lastImport,     setLastImport]     = useState<DeepkiImportRecord | null>(null);
  const [purging,        setPurging]        = useState(false);
  const purgingDelayed = useMinDuration(purging);
  const [purgeErr,          setPurgeErr]          = useState<string | null>(null);
  const [showPurgeConfirm,  setShowPurgeConfirm]  = useState(false);
  const [purgeCounts,       setPurgeCounts]       = useState<{ jour: number; heure: number } | null>(null);
  const [purgeCountsLoading, setPurgeCountsLoading] = useState(false);

  const lastDayOfMonth = new Date(consoToYear, consoToMonth, 0).getDate();
  const fromDate = `${consoFromYear}-${String(consoFromMonth).padStart(2,"0")}-01`;
  const toDate   = `${consoToYear}-${String(consoToMonth).padStart(2,"0")}-${String(lastDayOfMonth).padStart(2,"0")}`;

  const loadLastImport = useCallback(async () => {
    try {
      const li = await getDeepkiLastImport(meter.id);
      setLastImport(li);
      return li;
    } catch {
      return null; // pas bloquant pour l'affichage des données
    }
  }, [meter.id]);

  // `override` permet de forcer une plage (+ pas, pour deepki) précise pour ce chargement sans
  // attendre le prochain rendu (setConsoFromYear/setPas sont asynchrones) — utilisé juste après un
  // import pour caler la plage "Visualiser" et le pas sur ceux de l'import, sans passer par des
  // valeurs encore périmées.
  const loadConso = useCallback(async (override?: { fromYear:number; fromMonth:number; toYear:number; toMonth:number; pas?:Pas }) => {
    const fy = override?.fromYear ?? consoFromYear, fm = override?.fromMonth ?? consoFromMonth;
    const ty = override?.toYear   ?? consoToYear,   tm = override?.toMonth   ?? consoToMonth;
    const p  = override?.pas      ?? pas;
    const fromD = `${fy}-${String(fm).padStart(2,"0")}-01`;
    const lastDay = new Date(ty, tm, 0).getDate();
    const toD = `${ty}-${String(tm).padStart(2,"0")}-${String(lastDay).padStart(2,"0")}`;

    setConsoStale(false);
    setEntry({ data: [], loading: true, error: null, pas: p });
    try {
      if (isDeepki) {
        const fromMs = new Date(fromD + "T00:00:00Z").getTime();
        const toMs   = new Date(toD   + "T23:59:59Z").getTime();
        const res = await getDeepkiReadings(meter.id, fromMs, toMs, p);
        setEntry({ data: res.data, loading: false, error: null, pas: p });
        return;
      }

      const cleanId = encodeURIComponent(meter.id.replace(/^(PDL|PCE)\s*/i, "").replace(/\s+/g, "").trim());
      const fromKey = `${fy}-${String(fm).padStart(2,"0")}`;
      const toKey   = `${ty}-${String(tm).padStart(2,"0")}`;
      const years   = Array.from({length: ty - fy + 1}, (_, i) => fy + i);
      type GazResult = { data: [number,number][]; releves?: Releve[]; periodes?: Periode[] };
      // engie n'a pas de route unique {data,releves,periodes} comme enedis/grdf — deux routes
      // séparées (readings + index, cette dernière gaz uniquement, pas de notion "periodes"
      // irrégulières comme GRDF, les mois engie sont toujours calendaires).
      const fetchEngie = async (y: number): Promise<GazResult> => {
        const [readingsRes, indexRes] = await Promise.all([
          fetch(`${APP_CONFIG.tsIngestBaseUrl}/engie/readings/${cleanId}?years=${y}`)
            .then(r => r.ok ? r.json() as Promise<{ data: [number, number][] }> : { data: [] as [number, number][] })
            .catch(() => ({ data: [] as [number, number][] })),
          !isPdl
            ? fetch(`${APP_CONFIG.tsIngestBaseUrl}/engie/index/${cleanId}?years=${y}`)
                .then(r => r.ok ? r.json() as Promise<{ data: Releve[] }> : { data: [] as Releve[] })
                .catch(() => ({ data: [] as Releve[] }))
            : Promise.resolve({ data: [] as Releve[] }),
        ]);
        return { data: readingsRes.data, releves: indexRes.data, periodes: [] };
      };
      const results = await Promise.all(
        years.map(y => {
          if (isEngie) return fetchEngie(y);
          return fetch(isPdl
            ? `${APP_CONFIG.tsIngestBaseUrl}/enedis/meter/${cleanId}/conso/journaliere/${y}`
            : `${APP_CONFIG.tsIngestBaseUrl}/grdf/meter/${cleanId}/conso/mensuelle/${y}`)
            .then(r => r.ok ? r.json() as Promise<GazResult> : { data: [] as [number,number][] })
            .catch(() => ({ data: [] as [number,number][] }));
        })
      );
      const keyLen = isPdl ? 10 : 7;
      const upperBound = isPdl
        ? `${ty}-${String(tm).padStart(2,"0")}-31`
        : toKey;
      const merged = results.flatMap(r => r.data)
        .filter(([ts]) => { const dt=new Date(ts),y=dt.getFullYear(),mo=String(dt.getMonth()+1).padStart(2,"0"); const d=isPdl?`${y}-${mo}-${String(dt.getDate()).padStart(2,"0")}`:`${y}-${mo}`; return d >= fromKey.substring(0,keyLen) && d <= upperBound; })
        .sort(([a],[b]) => a - b);
      const releves = !isPdl
        ? results.flatMap(r => (r as GazResult).releves ?? [])
            .filter(r => { const d = new Date(r.t).toISOString().substring(0,7); return d >= fromKey && d <= toKey; })
            .sort((a,b) => a.t - b.t)
        : undefined;
      const periodes = !isPdl
        ? results.flatMap(r => (r as GazResult).periodes ?? [])
            .filter(r => { const d = new Date(r.debut).toISOString().substring(0,7); return d >= fromKey && d <= toKey; })
            .sort((a,b) => a.debut - b.debut)
        : undefined;
      setEntry({ data: merged, loading: false, error: null, releves, periodes, pas: p });
    } catch (e) {
      setEntry({ data: [], loading: false, error: (e as Error).message, pas: p });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meter.id, isPdl, isDeepki, consoFromYear, consoFromMonth, consoToYear, consoToMonth, pas]);

  // Cale les sélecteurs "Visualiser" sur la plus petite période (au mois près) englobant les
  // données d'un import — au montage (import déjà existant) comme juste après un nouvel import.
  const applyImportRange = useCallback((report: DeepkiImportRecord) => {
    const from = new Date(report.tsFrom * 1000);
    const to   = new Date(report.tsTo   * 1000);
    const range = {
      fromYear: from.getUTCFullYear(), fromMonth: from.getUTCMonth() + 1,
      toYear:   to.getUTCFullYear(),   toMonth:   to.getUTCMonth()   + 1,
    };
    setConsoFromYear(range.fromYear); setConsoFromMonth(range.fromMonth);
    setConsoToYear(range.toYear);     setConsoToMonth(range.toMonth);
    return range;
  }, []);

  useEffect(() => {
    (async () => {
      if (isDeepki) {
        // Pas d'appel /deepki/readings tant qu'aucun import n'existe — rien à afficher.
        const li = await loadLastImport();
        if (li) {
          const p = li.pas as Pas;
          setPas(p);
          loadConso({ ...applyImportRange(li), pas: p });
        } else {
          setActiveTab("importer");
        }
      } else {
        loadConso();
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `deepki_imports.rowCount` ne reflète que le dernier import — un PDL peut avoir des relevés
  // valides dans les deux tables (jour ET heure, cf. generateDailyFromHourly) au moment de la
  // purge, qui les supprime toutes les deux. On récupère les vrais comptes avant d'afficher
  // l'avertissement plutôt que de se fier à ce seul chiffre.
  const openPurgeConfirm = async () => {
    setPurgeCountsLoading(true);
    try {
      setPurgeCounts(await getDeepkiCounts(meter.id));
    } catch {
      setPurgeCounts(null); // pas bloquant — le message retombe sur rowCount du dernier import
    } finally {
      setPurgeCountsLoading(false);
      setShowPurgeConfirm(true);
    }
  };

  const handlePurge = async () => {
    setPurging(true); setPurgeErr(null);
    try {
      const res = await purgeDeepkiData(meter.id);
      showToast(`Données Deepki purgées ✓ (${res.readingsDeleted} relevés supprimés)`);
      await loadLastImport();
      loadConso();
    } catch (e) {
      setPurgeErr((e as Error).message);
    } finally {
      setPurging(false);
    }
  };

  // deepki : le pas peut être journalier ou horaire (aucun moyen de le savoir sans regarder les
  // données) — on agrège toujours par jour (somme) pour retomber sur le même format de tableau
  // que le reste de l'app ; pour les données non-deepki (déjà quotidiennes/mensuelles, clés
  // uniques), la somme équivaut à une simple lecture.
  //
  // deepki : champs UTC obligatoires (getUTCFullYear/getUTCMonth/getUTCDate) — les timestamps
  // stockés sont des heures locales naïves parsées comme UTC (cf. excel.ts), donc les méthodes
  // *locales* de Date (dépendantes du fuseau du navigateur) décaleraient les heures proches de
  // minuit vers le jour voisin dès que le fuseau local n'est pas UTC+0 (ex. Europe/Paris,
  // UTC+1/+2 selon la saison) — observé concrètement sur un import horaire : un jour de trop
  // agrégé (367 clés pour 366 jours attendus). Sans rapport avec les vrais timestamps UTC des
  // autres sources (dataenr), qui continuent d'utiliser les méthodes locales ci-dessous.
  // Le select "Pas" change immédiatement (pas d'onglet), mais entry.data reste celui du dernier
  // fetch tant que l'utilisateur n'a pas cliqué ↺ (même pattern que les sélecteurs de date, cf.
  // `stale`) — sans ce garde, un changement "Jour"→"Heure" affiche brièvement les anciennes
  // données journalières dans HoraireTable (rendu brut, sans agrégation) comme si c'était de la
  // donnée horaire. `dataFresh` détecte ce décalage ; tant qu'il n'est pas résolu, on traite les
  // données comme vides plutôt que d'afficher un résultat trompeur.
  const dataFresh = !isDeepki || entry?.pas === pas;
  const effectiveData: [number, number][] = entry && dataFresh ? entry.data : [];

  // engie n'a jamais de granularité journalière (même côté PDL/ÉLEC — un seul point par mois,
  // cf. loadConso ci-dessus) : traité comme le GAZ (mensuel) pour le regroupement/l'affichage,
  // pas comme un PDL enedis/deepki classique (quotidien).
  const isDailyGranularity = isPdl && !isEngie;

  const dataMap: Map<string,number> | null = entry
    ? effectiveData.reduce((map, [ts, v]) => {
        const dt = new Date(ts);
        const y  = isDeepki ? dt.getUTCFullYear() : dt.getFullYear();
        const mo = String((isDeepki ? dt.getUTCMonth() : dt.getMonth()) + 1).padStart(2,"0");
        const day = isDeepki ? dt.getUTCDate() : dt.getDate();
        const key = isDailyGranularity ? `${y}-${mo}-${String(day).padStart(2,"0")}` : `${y}-${mo}`;
        map.set(key, (map.get(key) ?? 0) + v);
        return map;
      }, new Map<string,number>())
    : null;

  const expectedDates = (() => {
    if (!fromDate || !toDate) return [];
    if (isDailyGranularity) {
      const dates: string[] = [];
      const cur = new Date(fromDate + "T00:00:00Z");
      const end = new Date(toDate   + "T00:00:00Z");
      while (cur <= end) { dates.push(cur.toISOString().substring(0,10)); cur.setUTCDate(cur.getUTCDate()+1); }
      return dates;
    } else {
      const months: string[] = [];
      let y = consoFromYear, mo = consoFromMonth;
      while (y < consoToYear || (y === consoToYear && mo <= consoToMonth)) {
        months.push(`${y}-${String(mo).padStart(2,"0")}`);
        mo++; if (mo > 12) { mo = 1; y++; }
      }
      return months;
    }
  })();
  const missingCount = dataMap ? expectedDates.filter(d=>!dataMap.has(d)).length : 0;
  const totalKwh = dataMap ? [...dataMap.values()].reduce((a,b)=>a+b,0) : 0;
  const unitLabel   = isDailyGranularity ? "jour"  : "mois";
  const unitLabelPl = isDailyGranularity ? "jours" : "mois";

  const handleExportCsv = () => {
    if (!entry || !dataFresh || entry.data.length === 0) return;
    const rows = entry.data.map(([ts, v]) => {
      const dt = new Date(ts);
      const y  = isDeepki ? dt.getUTCFullYear() : dt.getFullYear();
      const mo = String((isDeepki ? dt.getUTCMonth() : dt.getMonth()) + 1).padStart(2,"0");
      const day = isDeepki ? dt.getUTCDate() : dt.getDate();
      const d = isDailyGranularity?`${y}-${mo}-${String(day).padStart(2,"0")}`:`${y}-${mo}`;
      return `${d};${v.toFixed(3).replace(".",",")}`;
    });
    const csv = "Date;kWh\n" + rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `conso_${meter.id}_${consoFromYear}${String(consoFromMonth).padStart(2,"0")}-${consoToYear}${String(consoToMonth).padStart(2,"0")}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const showVisualiser = !isDeepki || activeTab === "visualiser";
  const pasOptions     = pasOptionsForSource(isDeepki);
  const showHoraire    = isPdl && pas === "heure";
  const showJournalier = !showHoraire && (isPdl || consoSubTab === "mensuel");

  // Détail par pas si dispo (fetché à l'ouverture de la confirmation, cf. openPurgeConfirm) ;
  // repli sur rowCount du dernier import si le fetch a échoué (pas bloquant pour la purge).
  const purgeMessage = purgeCounts
    ? `Tous les relevés du PDL "${meter.id}" seront définitivement supprimés : `
      + `${purgeCounts.jour} relevé${purgeCounts.jour > 1 ? "s" : ""} journalier${purgeCounts.jour > 1 ? "s" : ""} `
      + `et ${purgeCounts.heure} relevé${purgeCounts.heure > 1 ? "s" : ""} horaire${purgeCounts.heure > 1 ? "s" : ""}, `
      + `ainsi que l'historique des imports. Les fichiers déjà déposés sur le serveur ne sont pas effacés. Cette action est irréversible.`
    : `Tous les relevés importés pour le PDL "${meter.id}" (${lastImport?.rowCount ?? 0} lignes) et l'historique des imports seront définitivement supprimés. Les fichiers déjà déposés sur le serveur ne sont pas effacés. Cette action est irréversible.`;

  return (
    <Modal title={`Consommations — ${meter.id}`} onClose={onClose} wide fixedHeight>
      <div style={{padding:"16px 24px",overflowY:"auto",flex:1,minHeight:0}}>
        {isDeepki && (
          <TopTabs
            tabs={[{id:"visualiser",label:"📊 Visualiser les données"},{id:"importer",label:"📥 Importer"}]}
            active={activeTab} onChange={setActiveTab}
          />
        )}

        {isDeepki && activeTab === "importer" && (
          <DeepkiImportPanel
            pdl={meter as PdlRecord}
            siteId={site?.id ?? meter.siteId}
            lastImport={lastImport}
            showToast={showToast}
            onImported={(report) => {
              setLastImport(report);
              const p = report.pas as Pas;
              setPas(p);
              loadConso({ ...applyImportRange(report), pas: p });
              setActiveTab("visualiser");
            }}
          />
        )}

        {showVisualiser && (
          isDeepki && !lastImport ? (
            <div style={{fontSize:12,color:"var(--text-faint)",fontStyle:"italic"}}>
              Aucune donnée importée pour ce PDL — utilisez l'onglet "📥 Importer".
            </div>
          ) : (
            <>
              {isDeepki && lastImport && (
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,flexWrap:"wrap",gap:6}}>
                  <span style={{fontSize:11,color:"var(--text-faint)"}}>
                    Dernier import : {lastImport.rowCount} lignes ({lastImport.pas}), {fmtDate(lastImport.tsFrom)} → {fmtDate(lastImport.tsTo)},
                    {" "}le {new Date(lastImport.createdAt).toLocaleString("fr-FR")}
                  </span>
                  {canPurgeDeepki && (
                    <button onClick={openPurgeConfirm} disabled={purgeCountsLoading} className="btn btn-ghost btn-sm"
                      style={{fontSize:11,padding:"2px 8px",color:"var(--red)"}}>
                      🗑 {purgeCountsLoading ? "…" : "Purger les données"}
                    </button>
                  )}
                </div>
              )}

              {purgeErr && (
                <div style={{background:"#fdecea",border:"1px solid #f5c6cb",borderRadius:6,padding:"10px 14px",fontSize:12,color:"var(--red)",marginBottom:10}}>
                  ⚠ {purgeErr}
                </div>
              )}

              <ConsoRangeControls
                fromYear={consoFromYear} fromMonth={consoFromMonth} toYear={consoToYear} toMonth={consoToMonth}
                setFromYear={setConsoFromYear} setFromMonth={setConsoFromMonth}
                setToYear={setConsoToYear} setToMonth={setConsoToMonth} setStale={setConsoStale}
                stale={consoStale} loading={!!entry?.loading} canExport={!!entry && !entry.loading && dataFresh && entry.data.length > 0}
                onRefresh={loadConso} onExportCsv={handleExportCsv}
                pasControl={isPdl && isDeepki ? { options: pasOptions, value: pas, onChange: setPas } : undefined}
              />

              {!isPdl && (
                <PceSubTabs active={consoSubTab} onChange={setConsoSubTab}/>
              )}

              {entry?.error && (
                <div style={{color:"var(--red)",fontSize:12,marginBottom:8}}>⚠ {entry.error}</div>
              )}

              {entry && !entry.loading && !entry.error && (
                <>
                  {showJournalier && (
                    <SummaryStats
                      totalKwh={totalKwh} dataCount={dataMap!.size} expectedCount={expectedDates.length}
                      missingCount={missingCount} unitLabel={unitLabel} unitLabelPl={unitLabelPl}
                    />
                  )}
                  {showJournalier && (
                    <MensuelTable expectedDates={expectedDates} dataMap={dataMap!}/>
                  )}
                  {showHoraire && <HoraireTable data={effectiveData}/>}
                  {!isPdl && consoSubTab === "releves" && <RelevesTable periodes={entry.periodes}/>}
                  {!isPdl && consoSubTab === "index"   && <IndexTable releves={entry.releves}/>}
                </>
              )}

              {!entry && (
                <div style={{fontSize:12,color:"var(--text-faint)"}}>Chargement…</div>
              )}
            </>
          )
        )}
      </div>

      {showPurgeConfirm && (
        <ConfirmModal
          title="Purger les données Deepki ?"
          message={purgeMessage}
          confirmLabel="Purger" danger
          onCancel={()=>setShowPurgeConfirm(false)}
          onConfirm={handlePurge}
        />
      )}
      {purgingDelayed && <LoadingScreen message="Purge des données Deepki…" overlay/>}
    </Modal>
  );
}
